import { JourneyState, JourneyStep } from '../../components/wizard';
import { WizardPos } from '../../utils/wizardRoutes';

/** Locked future stages — honest, unclickable, and deliberately route-less. */
export const LOCKED_STAGES = ['Financing', 'Delivery', 'Installation', 'Contracting'];

/** Journey-rail step id → step number, for click handling. */
export const STEP_IDS: Record<string, number> = {
  upload: 1,
  criteria: 2,
  discovery: 3,
  outreach: 4,
  quotes: 5,
};

/** Step-3 sub-step id → sub-step number. */
export const SUB_STEP_IDS: Record<string, number> = {
  context: 1,
  validation: 2,
  discovery: 3,
};

export interface WizardStepMeta {
  title: string;
  subtitle: string;
}

export const WIZARD_STEP_META: Record<number, WizardStepMeta> = {
  1: {
    title: 'Upload BOM',
    subtitle: 'Add your BOM and supporting files — Robbie extracts the parts list automatically.',
  },
  2: {
    title: 'Sourcing Criteria',
    subtitle: 'Define your sourcing criteria to help Robbie find the best suppliers.',
  },
  3: {
    title: 'Supplier Discovery',
    subtitle: 'Review the AI-generated supplier recommendations and select the ones to contact.',
  },
  4: {
    title: 'Supplier Outreach',
    subtitle: 'Draft and send inquiries to your shortlisted suppliers and review responses in unified conversation threads.',
  },
  5: {
    title: 'Quotes & Proposals',
    subtitle: "Review Robbie's award recommendation, lock in your suppliers, and download the final RFQ and Smart BOM.",
  },
};

/**
 * Step 3 speaks per sub-step: the page title stays "Supplier Discovery" but
 * the subtitle names what this screen actually asks of the user (mock pages
 * 3a/3b/3c each carry their own line). Counts stay out of these strings —
 * the panels below the title own the live numbers.
 */
export const WIZARD_SUBSTEP_META: Record<number, WizardStepMeta> = {
  1: {
    title: 'Supplier Discovery',
    subtitle: 'Robbie analyzed your BOM — a few quick answers before he validates and finds suppliers.',
  },
  2: {
    title: 'Supplier Discovery',
    subtitle: 'Robbie reviewed your BOM — fix what blocks sourcing, then take the improvements you like.',
  },
  3: WIZARD_STEP_META[3],
};

const STEP_DEFS: { id: string; label: string }[] = [
  { id: 'upload', label: 'Upload BOM' },
  { id: 'criteria', label: 'Sourcing Criteria' },
  { id: 'discovery', label: 'Supplier Discovery' },
  { id: 'outreach', label: 'Supplier Outreach' },
  { id: 'quotes', label: 'Quotes & Proposals' },
];

const SUB_STEP_DEFS: { id: string; label: string }[] = [
  { id: 'context', label: 'Context' },
  { id: 'validation', label: 'Validation' },
  { id: 'discovery', label: 'Discovery' },
];

/**
 * Maps the RFQ's position onto the journey rail. `pos` is where the URL says
 * the user is looking; `data` (when provided) is the DATA-TRUTH of what has
 * actually been accomplished — a checkmark means the step's output exists
 * (BOM uploaded, shortlist made, every shortlisted supplier contacted, award
 * confirmed), never merely that the user navigated past the screen. Steps the
 * user can still navigate to (at or behind `frontier`) whose work is missing
 * render "visited" — clickable, numbered, with the reason tooltip; only steps
 * beyond the frontier render "upcoming" (not navigable yet). Without `data`
 * (legacy callers), falls back to position-based states.
 */
export const buildJourneySteps = (
  pos: WizardPos,
  reached: WizardPos,
  data?: { stepDone: Record<number, boolean>; stepReason: Record<number, string | undefined> },
  frontier?: number
): JourneyStep[] => {
  const maxStep = Math.max(pos.step, reached.step);
  const reachable = frontier ?? maxStep;
  return STEP_DEFS.map((def, i) => {
    const n = i + 1;
    const state: JourneyState = n === pos.step
      ? 'active'
      : data
        ? (data.stepDone[n] ? 'done' : n <= reachable ? 'visited' : 'upcoming')
        : (n < maxStep ? 'done' : 'upcoming');
    const step: JourneyStep = { id: def.id, label: def.label, state, reason: data?.stepReason[n] };
    if (n === 3 && pos.step === 3) {
      const reachedSub = reached.step > 3 ? 4 : reached.step === 3 ? reached.subStep : 1;
      const maxSub = Math.max(pos.subStep, reachedSub);
      // <= : the furthest sub-step actually reached stays revisitable (a user
      // stepping back from Discovery results to Validation must still see
      // Discovery as done/clickable, not locked "upcoming").
      step.subSteps = SUB_STEP_DEFS.map((sub, j) => ({
        ...sub,
        state: j + 1 === pos.subStep ? 'active' : j + 1 <= maxSub ? 'done' : 'upcoming',
      }));
    }
    return step;
  });
};
